import {
  atKeys,
  EmptyValueKind,
  isString,
  notEmpty,
  notNullish,
  parseArray,
  parseBoolean,
  parseDefault,
  parseNumber,
  parseRecord,
  parseString,
} from "@de-xima/xima-common-js-lang";
import type Framebus from "framebus";
import type { FramebusSubscriberArg } from "framebus/dist/lib/types.d.ts";
import jQuery from "jquery";

import type {
  IEmbeddedFramebusHandler,
  IFramebusHandler,
  IHideDialogOptions,
  IOpenedWindow,
  IShowDialogOptions,
  TIndex,
  TRemoteCommandParameters,
  TSeverity,
} from "./Header.ts";
import { showHelpWidget } from "./HelpWidget.ts";
import { openLoginDialog, setOpenLoginDialogAction } from "./Login.ts";
import {
  hideDialog,
  onGlobalLoadEnd,
  onGlobalLoadError,
  onGlobalLoadStart,
  showDialog,
  showNotification,
} from "./Notification.ts";
import { stringToSeverity } from "./NotificationHistory.ts";
import { DialogDataTableMaskPanel } from "./placeholder/maskpanel/DialogDataTableMaskPanel.ts";
import { FramebusChildMaskPanel } from "./placeholder/maskpanel/FramebusChildMaskPanel.ts";
import { FramebusTopMaskPanel } from "./placeholder/maskpanel/FramebusTopMaskPanel.ts";
import { PlaceholderMask } from "./placeholder/PlaceholderMask.ts";
import * as ProgressBar from "./ProgressBar.ts";
import { getWidgetByVar, runRemoteCommand } from "./Util.ts";
import { openWindow } from "./WindowManager.ts";

let CurrentFramebusHandler: IFramebusHandler | undefined;

export interface FramebusMessage {
  escape?: boolean | undefined;
  header?: string | undefined;
  message?: string | undefined;
  icon?: string | undefined;
}

/**
 * Invoke remote commands when requested.
 * @param framebus Framebus on which to register a listener.
 */
function listenToRemoteCommand(framebus: Framebus): void {
  // Invoke remote commands when requested
  framebus.on("remoteCommand", (data) => {
    const path =
      typeof data.path === "string" ? data.path.split(".") : Array.isArray(data.path) ? data.path.filter(isString) : [];
    const remoteCommand = atKeys(window, path);
    const params = parseArray(data.params);
    if (typeof remoteCommand === "function") {
      remoteCommand(params);
    }
  });
}

function handleGlobalConfirmDialogResponse(data: FramebusSubscriberArg) {
  const action = String(data.action);
  if (action === "confirmed") {
    const event = jQuery.Event("click") as JQuery.TriggeredEvent;
    event.target = document;
    event.currentTarget = document;
    if (notNullish(PrimeFaces.confirmSource)) {
      const element = PrimeFaces.confirmSource.get(0);
      if (element !== undefined) {
        const script = parseString(PrimeFaces.confirmSource.data("pfconfirmcommand"));
        PrimeFaces.csp.executeEvent(element, script, event);
      }
    }
  }
}

/**
 * @returns instance Fetches the current frame bus instance, when available.
 */
export function getInstance(): IFramebusHandler | undefined {
  return CurrentFramebusHandler;
}

/**
 * @param instance Sets the current frame bus instance.
 */
export function setInstance(instance: IFramebusHandler): void {
  CurrentFramebusHandler = instance;
}

/**
 * Base class for all frame bus handlers.
 */
export abstract class FrameBusHandler implements IFramebusHandler {
  /** @returns The framebus instance to use. */
  abstract getFramebus(): Framebus;

  /**
   * Opens a new window and returns an ID for the window. Can be used to refer to
   * that window later on. Registers the window with the framebus to enable
   * communication. Use {@link openWindow} directly if you do not need to communicate
   * via the framebus.
   * @param url URL for the window.
   * @param target Target frame.
   * @param features Optional features for the window.
   * @returns The opened window and a unique serializable ID to refer to that
   * window later on.
   */
  openWindow(url: string, target: string, features?: string): IOpenedWindow {
    const window = openWindow(url, target, features);
    if (window.window !== undefined && window.new) {
      this.getFramebus().include(window.window);
    }
    return window;
  }
}

/**
 * Base class that sets up common event listeners on the framebus required for a parent frame that contains iframes
 * which it must communicate. Includes listeners for handling loading dialogs etc.
 */
export abstract class FrameBusTopHandler extends FrameBusHandler {
  /**
   * Closes the window with the given ID.
   * @param windowId ID of the window to close.
   */
  emitCloseWindow(windowId: string): void {
    this.getFramebus().emit("closeWindow", { windowId: windowId });
  }

  /**
   * @param action Action to perform. Whether the user issued confirmation or
   * not.
   */
  emitGlobalConfirmDialogResponse(action: "confirmed" | "denied"): void {
    this.getFramebus().emit("globalConfirmDialogResponse", { action });
  }

  /**
   * @param scope Path to the remote command. Either a string with parts
   * separated by a dot, or an array of parts, e.g.
   * `"BackendCommand.Designer.myCommand"` or
   * `["BackendCommand", "Designer", "myCommand"]`
   * @param params Optional params to pass on to the remote command.
   */
  emitRemoteCommand(path: string | string[], params?: PrimeFaces.ajax.RemoteCommandParams): void {
    this.getFramebus().emit("remoteCommand", { params, path });
  }

  protected _init(): void {
    const framebus = this.getFramebus();

    // Register delegating placeholder mask handler for communicating with the
    // child when it needs placeholders
    PlaceholderMask.create({
      id: "default",
      initTriggerButtons: true,
      maskPanel: new FramebusTopMaskPanel({
        framebus: framebus,
        maskPanel: DialogDataTableMaskPanel.default(),
      }),
    });

    // Allow a frame to change the document title of the top window
    framebus.on("setDocumentTitle", (data) => {
      const title = parseString(data.title);
      document.title = title;
    });

    // Forward certain events on the document of a frame
    // Used by PrimeFaces e.g. to hide menus when the user clicks somewhere else
    framebus.on("documentEvent", (data) => {
      const type = parseString(data.type);
      if (notEmpty(type)) {
        jQuery(document).trigger(type);
      }
    });

    // Allow frames to show dialogs, possibly with effects
    framebus.on("showDialog", (data) => {
      const widgetVar = parseString(data.widgetVar);
      const options = parseRecord(data.options) as IShowDialogOptions;
      showDialog(widgetVar, options);
    });

    // Allow frames to hide dialogs
    framebus.on("hideDialog", (data) => {
      const widgetVar = parseString(data.widgetVar);
      const options = parseRecord(data.options) as IHideDialogOptions;
      hideDialog(widgetVar, options);
    });

    // Allow frames to interact with the loading dialog
    framebus.on("loadingDialog", (data) => {
      const action = parseString(data.action);
      switch (action) {
        case "show":
          getWidgetByVar("loadingDialog", PrimeFaces.widget.Dialog)?.show();
          break;
        case "hide":
          getWidgetByVar("loadingDialog", PrimeFaces.widget.Dialog)?.hide();
          break;
        default:
          console.warn("unknown loading dialog action", data.action);
      }
    });

    // Allow frames to interact with the loading dialog
    framebus.on("helpDialog", (data) => {
      const action = parseString(data.action);
      switch (action) {
        case "show":
          showHelpWidget(parseString(data.path));
          break;
        case "hide":
          getWidgetByVar("helpWidget", PrimeFaces.widget.Dialog)?.hide();
          break;
        default:
          console.warn("unknown help dialog action", data.action);
      }
    });

    // Allow frames to interact with the progress dialog
    framebus.on("progressDialog", (data) => {
      const action = parseString(data.action);
      const value = parseNumber(data.value);
      switch (action) {
        case "show":
          getWidgetByVar("progressDialog", PrimeFaces.widget.Dialog)?.show();
          ProgressBar.setValue(value ?? 0);
          break;
        case "hide":
          getWidgetByVar("progressDialog", PrimeFaces.widget.Dialog)?.hide();
          ProgressBar.setValue(value ?? 0);
          break;
        case "setValue":
          ProgressBar.setValue(value ?? 0);
          break;
        default:
          console.warn("Unknown loading dialog action", action);
      }
    });

    // Allow frames to interact with the support dialog
    framebus.on("supportDialog", (data) => {
      const action = parseString(data.action);
      switch (action) {
        case "show":
          runRemoteCommand(BackendCommand?.openSupportDialog).catch((e) =>
            console.error("Could not open suppport dialog", e),
          );
          break;
        case "hide":
          getWidgetByVar("supportDialogVar", PrimeFaces.widget.Dialog)?.hide();
          break;
        default:
          console.warn("Unknown support dialog action", action);
      }
    });

    // Allow frames to interact with the feedback dialog
    framebus.on("feedbackDialog", (data) => {
      const action = String(data.action);
      switch (action) {
        case "show":
          runRemoteCommand(BackendCommand?.openFeedbackDialog).catch((e) =>
            console.error("Could not open feedback dialog", e),
          );
          break;
        case "hide":
          getWidgetByVar("feedbackDialogVar", PrimeFaces.widget.Dialog)?.hide();
          break;
        default:
          console.warn("Unknown feedback dialog action", action);
      }
    });

    // Allow frames to interact with the session dialog
    framebus.on("sessionDialog", (data) => {
      const action = String(data.action);
      switch (action) {
        case "show":
          getWidgetByVar("fcSessionDialogVar", PrimeFaces.widget.Dialog)?.show();
          break;
        case "hide":
          getWidgetByVar("fcSessionDialogVar", PrimeFaces.widget.Dialog)?.hide();
          break;
        default:
          console.warn("Unknown session dialog action", action);
      }
    });

    // Allow frames to interact with the login dialog
    framebus.on("loginDialog", (data) => {
      const action = String(data.action);
      switch (action) {
        case "show":
          openLoginDialog();
          break;
        case "hide":
          getWidgetByVar("fcLoginDialogVar", PrimeFaces.widget.Dialog)?.hide();
          break;
        default:
          console.warn("Unknown login dialog action", action);
      }
    });

    // Allow frames to interact with the download dialog
    framebus.on("downloadDialog", (data) => {
      const action = String(data.action);
      switch (action) {
        case "show":
          getWidgetByVar("downloadDialog", PrimeFaces.widget.Dialog)?.show();
          break;
        case "hide":
          getWidgetByVar("downloadDialog", PrimeFaces.widget.Dialog)?.hide();
          break;
        default:
          console.warn("Unknown login download action", action);
      }
    });

    framebus.on("updatePlaceolders", (data) => {
      const showLoading = parseBoolean(data.showLoading);
      if (showLoading) {
        // TODO With PF 10+, this actually returns a promise
        BackendCommand?.updatePlaceholderContentLoad();
      } else {
        // TODO With PF 10+, this actually returns a promise
        BackendCommand?.updatePlaceholderContent();
      }
    });

    // Allow frames to interact with the session dialog
    framebus.on("exceptionDialog", (data) => {
      const action = String(data.action);
      switch (action) {
        case "show":
          getWidgetByVar("fcExceptionDialogVar", PrimeFaces.widget.Dialog)?.show();
          break;
        case "hide":
          getWidgetByVar("fcExceptionDialogVar", PrimeFaces.widget.Dialog)?.hide();
          break;
        default:
          console.warn("Unknown feedback exception action", action);
      }
    });

    // Allow frames to interact with the session dialog
    framebus.on("globalConfirmDialog", (data) => {
      const action = parseString(data.action);
      const message = parseRecord(data.message);
      const doEscape = parseBoolean(message.escape);
      const header = parseString(message.header);
      const text = parseString(message.message);
      const icon = parseString(message.icon);
      switch (action) {
        case "show": {
          const source = jQuery(document.createElement("div"));
          source.data(
            "pfconfirmcommand",
            "Formcycle.FramebusHandler.getInstance().emitGlobalConfirmDialogResponse('confirmed')",
          );
          PrimeFaces.dialog.DialogHandler.confirm({
            escape: doEscape,
            header: header,
            icon: icon,
            message: text,
            source: source,
          });
          break;
        }
        case "hide":
          PrimeFaces.confirmDialog?.hide();
          break;
        default:
          console.warn("Unknown feedback exception action", action);
      }
    });

    // Show message(s)
    framebus.on("showMessage", (data) => {
      const summary = parseString(data.summary);
      const detail = parseString(data.detail);
      const severity = parseString(data.severity);
      const saveInSession = parseDefault(data.saveInSession, parseBoolean, true);
      showNotification(
        {
          detail: detail,
          severity: stringToSeverity(severity),
          summary: summary,
        },
        undefined,
        saveInSession,
      );
    });

    framebus.on("globalLoadStart", (data) => {
      const widgetVar = parseDefault(data.widgetVar, parseString, undefined, EmptyValueKind.any);
      onGlobalLoadStart(widgetVar);
    });

    framebus.on("globalLoadEnd", (data) => {
      const widgetVar = parseDefault(data.widgetVar, parseString, undefined, EmptyValueKind.any);
      onGlobalLoadEnd(widgetVar);
    });

    framebus.on("globalLoadError", (data) => {
      const statusText = parseString(data.statusText);
      const summary = parseString(data.summary);
      const detail = parseString(data.detail);
      onGlobalLoadError(statusText, summary, detail);
    });

    framebus.on("showMessages", (data) => {
      const messages = parseArray(data.messages);
      const saveInSession = parseDefault(data.saveInSession, parseBoolean, true);
      for (const message of messages) {
        const msg = parseRecord(message);
        const summary = parseString(msg.summary);
        const detail = parseString(msg.detail);
        const severity = parseString(msg.severity);
        showNotification(
          {
            detail: detail,
            severity: stringToSeverity(severity),
            summary: summary,
          },
          undefined,
          saveInSession,
        );
      }
    });

    // Invoke remote commands when requested
    listenToRemoteCommand(framebus);
  }
}

/**
 * Base class for frame bus handler of pages inside an iframe that must
 * communicate with its parent to show loading dialogs etc.
 */
export abstract class FrameBusChildHandler extends FrameBusHandler implements IEmbeddedFramebusHandler {
  /**
   * Closes the window with the given ID.
   * @param windowId ID of the window to close.
   */
  emitCloseWindow(windowId: string): void {
    this.getFramebus().emit("closeWindow", { windowId: windowId });
  }

  /**
   * @param e Event that occurred on the document.
   */
  emitDocumentEvent(e: JQuery.TriggeredEvent): void {
    this.getFramebus().emit("documentEvent", {
      type: e.type,
    });
  }

  /**
   * Sends a command to the top download dialog.
   * @param action `show` to open the download dialog, `hide` to close it.
   */
  emitDownloadDialog(action: "show" | "hide"): void {
    this.getFramebus().emit("downloadDialog", { action });
  }

  /**
   * Sends a command to the top exception dialog.
   * @param action `show` to open the exception dialog, `hide` to close it.
   */
  emitExceptionDialog(action: "show" | "hide"): void {
    this.getFramebus().emit("exceptionDialog", { action });
  }

  /**
   * Sends a command to the top feedback dialog.
   * @param action `show` to open the feedback dialog, `hide` to close it.
   */
  emitFeedbackDialog(action: "show" | "hide"): void {
    this.getFramebus().emit("feedbackDialog", { action });
  }

  /**
   * Sends a command to the top global confirm dialog.
   * @param action `show` to open the global confirm dialog, `hide` to close it.
   * @param message Message and icon to show in the confirm dialog.
   */
  emitGlobalConfirmDialog(action: "show" | "hide", message: FramebusMessage = {}): void {
    this.getFramebus().emit("globalConfirmDialog", { action, message });
  }

  /**
   * Informs the parent page about the global AJAX loading start event
   * (`p:ajaxStatus ...`)
   * @param dialogWidgetVar Widget variable for the loading dialog.
   */
  emitGlobalLoadEnd(dialogWidgetVar: string = "loadingDialog"): void {
    this.getFramebus().emit("globalLoadEnd", { widgetVar: dialogWidgetVar });
  }

  /**
   * Informs the parent page about the global AJAX loading start event
   * (`p:ajaxStatus ...`)
   * @param statusText Status text of the XHR request.
   * @param summary Localized message to show as the summary of the message
   * informing the user about the error.
   * @param detail Localized message to show as the detail of the message
   * informing the user about the error.
   */
  emitGlobalLoadError(statusText: string, summary: string, detail: string): void {
    this.getFramebus().emit("globalLoadError", { detail, statusText, summary });
  }

  /**
   * Informs the parent page about the global AJAX loading start event
   * (`p:ajaxStatus ...`)
   * @param dialogWidgetVar Widget variable for the loading dialog.
   */
  emitGlobalLoadStart(dialogWidgetVar: string = "loadingDialog"): void {
    this.getFramebus().emit("globalLoadStart", { widgetVar: dialogWidgetVar });
  }

  /**
   * Sends a command to open the top loading dialog.
   * @param action `show` to open the loading dialog, `hide` to close it.
   */
  emitLoadingDialog(action: "show" | "hide"): void {
    this.getFramebus().emit("loadingDialog", { action });
  }

  /**
   * Sends a command  to show the given dialog.
   * @param widgetVar Widget var of a PrimeFaces dialog component.
   * @param options Optional settings for how the dialog is shown.
   */
  emitShowDialog(widgetVar: string, options: IShowDialogOptions): void {
    this.getFramebus().emit("showDialog", { options, widgetVar });
  }

  /**
   * Sends a command  to hide the given dialog when it is currently visible.
   * @param widgetVar Widget var of a PrimeFaces dialog component.
   * @param options Optional settings for how the dialog is hidden.
   */
  emitHideDialog(widgetVar: string, options: IHideDialogOptions): void {
    this.getFramebus().emit("hideDialog", { options, widgetVar });
  }

  /**
   * Sends a command to open the top help dialog.
   * @param action `show` to open the loading dialog, `hide` to close it.
   * @param path Path for the help dialog that controls which help pages to suggest.
   */
  emitHelpDialog(action: "show" | "hide", path: string): void {
    this.getFramebus().emit("helpDialog", { action, path });
  }

  /**
   * Sends a command to open the top login dialog.
   * @param action `show` to open the login dialog, `hide` to close it.
   */
  emitLoginDialog(action: "show" | "hide"): void {
    this.getFramebus().emit("loginDialog", { action });
  }

  /**
   * When an error occurred in the child frame that resulted in the frame not
   * functioning properly anymore, sends a message to the parent frame so that
   * it can show that error.
   * @param errorMessage Optional error message to send.
   */
  emitPageError(errorMessage: string = ""): void {
    this.getFramebus().emit("pageError", {
      errorMessage: errorMessage,
      pageId: this.getPageId(),
    });
  }

  /**
   * Shows the progress dialog with the given progress value, sets the progress
   * value, or hides the progress dialog.
   * @param action Action to perform.
   * @param progressValue New value for the progress dialog.
   */
  emitProgressDialog(action: "show" | "setValue" | "hide", progressValue: number = 0): void {
    this.getFramebus().emit("progressDialog", { action: action, value: progressValue });
  }

  /**
   * Meant for use in XHTML pages. When called via script, use
   * {@link emitRemoteCommandFn}, which is type safe.
   * @param scope Path to the remote command. Either a string with parts
   * separated by a dot, or an array of parts, e.g.
   * `"BackendCommand.Designer.myCommand"` or
   * `["BackendCommand", "Designer", "myCommand"]`
   * @param params Optional params to pass on to the remote command.
   */
  emitRemoteCommand(path: string | string[], params?: PrimeFaces.ajax.RemoteCommandParams): void {
    this.getFramebus().emit("remoteCommand", { params, path });
  }

  /**
   * Type safe version of {@link emitRemoteCommand}.
   * @param scope Path to the remote command. An array of parts, e.g.
   * `"BackendCommand.Designer.myCommand"` or `["BackendCommand", "Designer", "myCommand"]`
   * @param params Optional params to pass on to the remote command.
   */
  emitRemoteCommandSafe<K extends string[]>(params: TRemoteCommandParameters<TIndex<Window, K>>, ...path: K): void {
    this.getFramebus().emit("remoteCommand", { params, path });
  }

  /**
   * Sends a command to the top session dialog.
   * @param action `show` to open the session dialog, `hide` to close it.
   */
  emitSessionDialog(action: "show" | "hide"): void {
    this.getFramebus().emit("sessionDialog", { action });
  }

  /**
   * Sets the title of the main document to the given value.
   * @param title The new title for the document.
   */
  emitSetDocumentTitle(title: string): void {
    this.getFramebus().emit("setDocumentTitle", { title: title });
  }

  /**
   * Emits an event to show a (faces) message.
   * @param severity Severity of the message.
   * @param summary Summary of the message.
   * @param detail Detail of the message.
   * @param saveInSession Whether it should be saved in the session.
   */
  emitShowMessage(severity: TSeverity, summary: string, detail: string, saveInSession?: boolean): void {
    this.getFramebus().emit("showMessage", { detail, saveInSession, severity, summary });
  }

  /**
   * Emits an event to show one ore more (faces) messages.
   * @param messages Messages to show.
   * @param saveInSession Whether the messags should be saved in the session.
   */
  emitShowMessages(messages: { severity: TSeverity; summary: string; detail: string }[], saveInSession: boolean): void {
    this.getFramebus().emit("showMessages", { messages, saveInSession });
  }

  /**
   * Sends a command to the top support dialog.
   * @param action `show` to open the support dialog, `hide` to close it.
   */
  emitSupportDialog(action: "show" | "hide"): void {
    this.getFramebus().emit("supportDialog", { action });
  }

  /**
   * Updates the placeholder dialog.
   * @param forceLoading Whether to show the loading dialog.
   */
  emitUpdatePlaceholders(showLoading: boolean = false): void {
    this.getFramebus().emit("updatePlaceolders", { showLoading });
  }

  protected _init(): void {
    const framebus = this.getFramebus();

    // Register delegating placeholder mask handler for communicating with the
    // parent placeholder dialog
    PlaceholderMask.create({
      id: "default",
      initTriggerButtons: true,
      maskPanel: new FramebusChildMaskPanel({ framebus }),
    });

    // Register login dialog handler that forwards to the parent
    setOpenLoginDialogAction(() => this.emitLoginDialog("show"));

    // Create and register custom confirm dialog to forward confirm requests to the parent
    const confirmDialog = new PrimeFaces.widget.ConfirmDialog({
      id: "foobar",
      responsive: false,
      widgetVar: "foobar",
    });
    confirmDialog.showMessage = (msg) => {
      if (notEmpty(msg.beforeShow)) {
        PrimeFaces.csp.eval(msg.beforeShow);
      }
      this.emitGlobalConfirmDialog("show", {
        escape: msg.escape,
        header: msg.header,
        icon: msg.icon,
        message: msg.message,
      });
    };
    confirmDialog.hide = () => {
      this.emitGlobalConfirmDialog("hide");
    };

    PrimeFaces.confirmDialog = confirmDialog;

    // Delegate certain events to the parent window, important for overlays etc.
    // E.g. overlays register a listener and close themselves when the user
    // clicks outside the overlay
    jQuery(document).on("click", (e) => this.emitDocumentEvent(e));
    jQuery(document).on("mousedown", (e) => this.emitDocumentEvent(e));

    // Forward confirm dialog response to the handler, if present
    framebus.on("globalConfirmDialogResponse", (data) => handleGlobalConfirmDialogResponse(data));

    // Invoke remote commands when requested
    listenToRemoteCommand(framebus);
  }

  /**
   * @returns The ID of this page, used e.g. for identifying this page to the
   * parent frame.
   */
  abstract getPageId(): string;
}
